'use client';

import { useState } from 'react';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { INDUSTRY_FAQS } from '@/lib/industry-faqs';
import { cn } from '@/lib/utils';

const CONTENT = {
  en: {
    heroTitleA: 'AI customer support',
    heroTitleB: 'for travel & hospitality',
    heroSubtitle:
      "Your guests book tours at midnight. RV renters need help on the road. Questions don't wait for business hours. Agent 5 handles bookings, modifications, and customer questions instantly—so your team focuses on creating amazing experiences.",
    heroCta: 'See how it works for travel',
    benefitsTitle: 'Support customers from booking to checkout',
    benefitsSubtitle:
      'Handle every stage of the customer journey—automatically and accurately.',
    benefits: [
      {
        title: 'Booking confirmations and modifications',
        description:
          'Customers change dates, add participants, or request specific time slots. Agent 5 checks availability, processes changes, and sends updated confirmations—instantly, any time.',
      },
      {
        title: 'Tour details and special requests',
        description:
          'What should I bring? Is the tour wheelchair accessible? Can we accommodate dietary restrictions? Agent 5 answers tour specifics, logs special requests, and ensures nothing falls through the cracks.',
      },
      {
        title: 'Weather cancellations and rebooking',
        description:
          'Storm cancelled the tour? Heavy rain makes RV travel unsafe? Agent 5 notifies customers proactively, offers alternative dates, and processes cancellations or rebookings based on your policies.',
      },
      {
        title: 'Equipment guides and RV questions',
        description:
          "How do I connect the water? Where's the fuse box? What does this light mean? For RV and camper rentals, Agent 5 provides walkthroughs, troubleshooting guides, and answers equipment questions on the road.",
      },
      {
        title: 'Directions and check-in instructions',
        description:
          "Where do we meet? What's the parking situation? When should we arrive? Agent 5 sends location details, parking info, and check-in instructions automatically as the date approaches.",
      },
      {
        title: 'Group coordination and multi-booking',
        description:
          'Large groups need multiple vehicles or tour slots. Agent 5 coordinates bookings, manages group inquiries, and ensures everyone has the same information—no back-and-forth chaos.',
      },
      {
        title: 'Cancellations and refund processing',
        description:
          "Customer can't make it? Policy allows cancellation within 48 hours? Agent 5 applies your cancellation rules automatically, processes refunds, and offers rebooking options.",
        statLabel: 'Customer Support',
        statSublabel: 'When travel plans change',
      },
    ],
    comparison: [
      {
        metric: 'Booking Modifications',
        agent5: 'Instant changes 24/7',
        traditional: 'Email only, 24hr response',
      },
      {
        metric: 'Weather Cancellations',
        agent5: 'Auto-rebooking options',
        traditional: 'Manual coordination',
      },
      {
        metric: 'Group Bookings',
        agent5: 'Multi-party coordination',
        traditional: 'Phone tag chaos',
      },
      {
        metric: 'Equipment Questions',
        agent5: 'Instant specs & guides',
        traditional: 'Wait for specialist',
      },
      {
        metric: 'Refund Requests',
        agent5: 'Policy-based automation',
        traditional: 'Manual approval delays',
      },
      {
        metric: 'Peak Season Support',
        agent5: 'Scales instantly',
        traditional: 'Overwhelmed team',
      },
      {
        metric: 'Language Support',
        agent5: '100+ languages',
        traditional: 'English + 2-3 others',
      },
      {
        metric: 'Cost per Inquiry',
        agent5: '$0.75-$0.90 per resolution',
        traditional: '$7-15 per ticket',
      },
    ],
    comparisonTitle: 'Why travel companies choose Agent 5',
    comparisonSubtitle:
      'Purpose-built for businesses where customer experience is everything.',
    colWhatMatters: 'What Matters in Travel',
    colProduct: 'Agent 5',
    colTraditional: 'Traditional Support',
    faqTitle: 'Questions from travel operators',
    faqSubtitle:
      'Real answers for tour operators, rental companies, and hospitality businesses.',
    faq: INDUSTRY_FAQS['travel-hospitality'].en,
  },
  ar: {
    heroTitleA: 'دعم عملاء بالذكاء الاصطناعي',
    heroTitleB: 'للسفر والضيافة',
    heroSubtitle:
      'ضيفك يحجز جولة منتصف الليل. ومستأجر الكرفان واقف على طريق بلا تغطية، يحتاج يعرف كيف يوصّل الماء. والأسئلة ما تنتظر الدوام. النظام يرد على الحجوزات والتعديلات وأسئلة العملاء فوراً — وفريقك يتفرّغ للتجربة نفسها.',
    heroCta: 'ورّيني كيف يشتغل للسفر',
    benefitsTitle: 'ادعم ضيوفك من الحجز حتى المغادرة',
    benefitsSubtitle:
      'من أول استفسار لآخر تقييم — كل مرحلة مغطّاة، تلقائياً وبدقة.',
    benefits: [
      {
        title: 'تأكيدات الحجز وتعديلاته',
        description:
          'تأجيل يومين، شخصان إضافيان، موعد الصباح بدل العصر. يتحقق النظام من التوفّر، وينفّذ التغيير، ويرسل التأكيد المحدّث — فوراً، وفي أي ساعة.',
      },
      {
        title: 'تفاصيل الجولة والطلبات الخاصة',
        description:
          'وش ألبس؟ وهل الجولة مهيّأة للكرسي المتحرك؟ وهل يوجد خيار لحساسية غذائية؟ يجيب النظام عن تفاصيل الجولة، ويسجّل الطلبات الخاصة، وما يدع طلباً واحداً يضيع.',
      },
      {
        title: 'إلغاءات الطقس وإعادة الحجز',
        description:
          'ألغت العاصفة جولة الصباح؟ وجعل المطر الطريق مو آمن للكرفان؟ يبلّغ النظام العملاء قبل ما يسألون، ويعرض تواريخ بديلة، ويعالج الإلغاء أو إعادة الحجز حسب سياستك.',
      },
      {
        title: 'أدلة المعدات وأسئلة الكرفانات',
        description:
          'كيف أوصّل الماء؟ ووين صندوق المصاهر؟ ووش يعني هذا الضوء الأحمر؟ في الكرفانات والمركبات الترفيهية، يرشد النظام العميل خطوة بخطوة، ويجيب عن أسئلة المعدات وهو على الطريق.',
      },
      {
        title: 'الاتجاهات وتعليمات تسجيل الوصول',
        description:
          'وين نلتقي بالضبط؟ وهل تتوفّر مواقف؟ ومتى لازم نوصل؟ يرسل النظام الموقع وتفاصيل المواقف وتعليمات تسجيل الوصول تلقائياً كلما اقترب الموعد.',
      },
      {
        title: 'تنسيق المجموعات والحجز المتعدد',
        description:
          'مجموعة كبيرة تحتاج أكثر من مركبة أو أكثر من موعد. ينسّق النظام الحجوزات، ويرد على استفسارات المجموعة، ويبقي الجميع على المعلومة نفسها — بلا سلسلة رسائل ما تنتهي.',
      },
      {
        title: 'الإلغاءات ومعالجة الاسترداد',
        description:
          'العميل ما يقدر يحضر؟ وسياستك تسمح بالإلغاء خلال ٤٨ ساعة؟ يطبّق النظام القاعدة تلقائياً، ويعيد المبلغ، ويعرض بدائل لإعادة الحجز.',
        statLabel: 'دعم العملاء',
        statSublabel: 'لمّا تتغيّر خطط السفر',
      },
    ],
    comparison: [
      {
        metric: 'تعديلات الحجز',
        agent5: 'تغييرات فورية ٢٤/٧',
        traditional: 'بالإيميل بس، ورد بعد ٢٤ ساعة',
      },
      {
        metric: 'إلغاءات الطقس',
        agent5: 'خيارات إعادة حجز تلقائية',
        traditional: 'تنسيق يدوي',
      },
      {
        metric: 'حجوزات المجموعات',
        agent5: 'تنسيق بين عدة أطراف',
        traditional: 'فوضى مكالمات ما تنتهي',
      },
      {
        metric: 'أسئلة المعدات',
        agent5: 'مواصفات وأدلة فورية',
        traditional: 'انتظار أخصائي',
      },
      {
        metric: 'طلبات الاسترداد',
        agent5: 'أتمتة حسب السياسة',
        traditional: 'تأخير الموافقات اليدوية',
      },
      {
        metric: 'الدعم في مواسم الذروة',
        agent5: 'يتوسّع فوراً',
        traditional: 'فريق منهك',
      },
      {
        metric: 'دعم اللغات',
        agent5: '+١٠٠ لغة',
        traditional: 'الإنجليزية و٢-٣ لغات غيرها',
      },
      {
        metric: 'التكلفة لكل استفسار',
        agent5: '٠,٩٩ دولار لكل حل',
        traditional: '٧-١٥ دولاراً لكل تذكرة',
      },
    ],
    comparisonTitle: 'ليش تختار شركات السفر النظام',
    comparisonSubtitle: 'مبني لشركات تكون التجربة فيها هي المنتج.',
    colWhatMatters: 'اللي يهم في قطاع السفر',
    colProduct: 'النظام',
    colTraditional: 'الدعم التقليدي',
    faqTitle: 'أسئلة من مشغّلي السفر',
    faqSubtitle: 'أجوبة صريحة لمشغّلي الجولات وشركات التأجير والضيافة.',
    faq: INDUSTRY_FAQS['travel-hospitality'].ar,
  },
} as const;

export default function TravelHospitalityPage() {
  const { locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonStatus = [
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'neutral' },
    { agent5: 'best', traditional: 'worst' },
  ];

  const comparisonData = c.comparison.map((row, i) => ({
    metric: row.metric,
    agent5: { value: row.agent5, status: comparisonStatus[i].agent5 },
    traditional: {
      value: row.traditional,
      status: comparisonStatus[i].traditional,
    },
  }));

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {c.heroTitleA}
            <br />
            {c.heroTitleB}
          </>
        }
        subtitle={c.heroSubtitle}
        ctaText={c.heroCta}
      />

      <Logos />

      <BenefitsSection
        title={c.benefitsTitle}
        subtitle={c.benefitsSubtitle}
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="#000000"
        benefits={[
          {
            title: c.benefits[0].title,
            description: c.benefits[0].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: c.benefits[1].title,
            description: c.benefits[1].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: c.benefits[2].title,
            description: c.benefits[2].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: c.benefits[3].title,
            description: c.benefits[3].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: c.benefits[4].title,
            description: c.benefits[4].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: c.benefits[5].title,
            description: c.benefits[5].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: c.benefits[6].title,
            description: c.benefits[6].description,
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '24/7',
              label: c.benefits[6].statLabel,
              sublabel: c.benefits[6].statSublabel,
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {c.comparisonTitle}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {c.comparisonSubtitle}
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {c.colWhatMatters}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {c.colProduct}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {c.colTraditional}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/50"
                      onMouseEnter={() => setHoveredModel(row.metric)}
                      onMouseLeave={() => setHoveredModel(null)}
                    >
                      <TableCell className="text-foreground font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-green-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.agent5.status === 'best' && (
                            <CheckCircle className="size-4 text-green-600" />
                          )}
                          {row.agent5.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-foreground text-sm">
                            {row.agent5.value}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-red-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.traditional.status === 'worst' && (
                            <XCircle className="size-4 text-red-600" />
                          )}
                          {row.traditional.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-muted-foreground text-sm">
                            {row.traditional.value}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={c.faqTitle}
        subtitle={c.faqSubtitle}
        items={c.faq.map((item) => ({ question: item.q, answer: item.a }))}
      />

      <Testimonials />
    </>
  );
}
